/**
 * WojChat - Adrian Wojnarowski Basketball Scouting AI
 *
 * A specialized chat interface for basketball recruiting and scouting,
 * featuring the Woj persona with Moneyball-style player evaluations.
 *
 * Features:
 * - Standard chat with Woj persona
 * - Deep research for comprehensive analysis
 * - Quick player evaluations
 * - St. Bonaventure transfer portal focus
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  Send,
  Search,
  UserCheck,
  Trash2,
  Loader2,
  MessageSquare,
  TrendingUp,
  Target,
  DollarSign,
  AlertTriangle,
  CheckCircle2,
  User,
  Newspaper,
  Globe,
  AtSign,
  BarChart3,
  FileText,
  Zap,
} from 'lucide-react';
import { marked } from 'marked';
import useWojChat from '../../hooks/useWojChat';

// Woj avatar/branding
const WOJ_AVATAR = '\u{1F3C0}';
const WOJ_NAME = 'Adrian Wojnarowski';
const WOJ_TITLE = 'GM, St. Bonaventure Basketball';

// Sample prompts for different modes
const CHAT_PROMPTS = [
  { text: 'What makes a Bonaventure player?', icon: '\u{1F3AF}' },
  { text: 'Analyze the A-10 transfer portal', icon: '\u{1F4CA}' },
  { text: 'How do we compete with high-majors?', icon: '\u{1F4B0}' },
  { text: 'Best value signings this year?', icon: '\u{1F50D}' },
];

const RESEARCH_TOPICS = [
  'A-10 transfer portal trends 2025',
  'Mid-major NIL competitive landscape',
  'Graduate transfer market analysis',
  'High-major players behind NBA draft picks',
];

// Exa search type options
const EXA_SEARCH_TYPES = [
  { value: 'scoop', label: 'Breaking News', icon: Newspaper, description: 'Transfer news, commitments, scoops' },
  { value: 'socials', label: 'Social Media', icon: AtSign, description: 'Twitter, Instagram, profiles' },
  { value: 'news', label: 'Recent News', icon: Globe, description: 'Latest articles and coverage' },
  { value: 'stats', label: 'Stats & Analytics', icon: BarChart3, description: 'KenPom, Barttorvik, etc.' },
  { value: 'evaluation', label: 'Scouting Reports', icon: FileText, description: 'Evaluations and analysis' },
  { value: 'nil', label: 'NIL Intel', icon: DollarSign, description: 'NIL deals and valuations' },
];

/**
 * Message component with markdown rendering
 */
const MessageBubble = ({ message }) => {
  const isUser = message.role === 'user';
  const isResearch = message.isResearch;
  const isError = message.isError;

  // Render markdown content
  const renderContent = (content) => {
    try {
      const html = marked(content, { breaks: true, gfm: true });
      return <div dangerouslySetInnerHTML={{ __html: html }} />;
    } catch {
      return <p>{content}</p>;
    }
  };

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
      {/* Avatar */}
      <div className={`
        flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-lg
        ${isUser
          ? 'bg-[#E8B800]/20'
          : 'bg-orange-500/20'
        }
      `}>
        {isUser ? <User className="w-4 h-4 text-[#E8B800]" /> : WOJ_AVATAR}
      </div>

      {/* Content */}
      <div className={`flex-1 max-w-[85%] ${isUser ? 'text-right' : 'text-left'}`}>
        {/* Research status badge */}
        {isResearch && message.researchStatus && (
          <div className={`
            inline-flex items-center gap-1.5 text-xs mb-1 px-2 py-0.5 rounded-full
            ${message.researchStatus === 'in_progress'
              ? 'bg-blue-500/20 text-blue-400'
              : message.researchStatus === 'completed'
                ? 'bg-green-500/20 text-green-400'
                : 'bg-red-500/20 text-red-400'
            }
          `}>
            {message.researchStatus === 'in_progress' && (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                <span>Deep Research in Progress</span>
              </>
            )}
            {message.researchStatus === 'completed' && (
              <>
                <CheckCircle2 className="h-3 w-3" />
                <span>Research Complete</span>
              </>
            )}
            {message.researchStatus === 'failed' && (
              <>
                <AlertTriangle className="h-3 w-3" />
                <span>Research Failed</span>
              </>
            )}
          </div>
        )}

        {/* Message bubble */}
        <div className={`
          inline-block rounded-2xl px-4 py-2.5
          ${isUser
            ? 'bg-[#E8B800] text-white'
            : isError
              ? 'bg-red-900/30 text-red-300'
              : 'bg-[#1A1A1A] text-[#F5F5F5]'
          }
          ${isResearch && message.researchStatus === 'in_progress' ? 'animate-pulse' : ''}
        `}>
          {isUser ? (
            <p className="text-sm whitespace-pre-wrap">{message.content}</p>
          ) : (
            <div className="prose prose-sm prose-invert max-w-none text-inherit">
              {renderContent(message.content)}
            </div>
          )}
        </div>

        {/* Timestamp */}
        <p className="text-[10px] mt-1 text-[#555555]">
          {new Date(message.timestamp).toLocaleTimeString()}
        </p>
      </div>
    </div>
  );
};

/**
 * Tab button component
 */
const TabButton = ({ active, onClick, children, icon: Icon }) => {
  return (
    <button
      onClick={onClick}
      className={`
        flex-1 flex items-center justify-center gap-1.5 py-2 px-3 text-xs font-medium rounded-lg transition-all
        ${active
          ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
          : 'text-[#999999] hover:bg-[rgba(255,255,255,0.04)]'
        }
      `}
    >
      {Icon && <Icon className="w-3.5 h-3.5" />}
      {children}
    </button>
  );
};

/**
 * Main WojChat component
 */
export default function WojChat() {
  const {
    messages,
    isLoading,
    activeResearch,
    activeExaSearch,
    sendMessage,
    startResearch,
    evaluatePlayer,
    searchScoop,
    comprehensiveSearch,
    clearChat
  } = useWojChat();

  const [inputValue, setInputValue] = useState('');
  const [researchQuery, setResearchQuery] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [scoopQuery, setScoopQuery] = useState('');
  const [scoopSearchType, setScoopSearchType] = useState('scoop');
  const [activeTab, setActiveTab] = useState('chat');
  const scrollRef = useRef(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [messages]);

  const handleChatSubmit = (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;
    sendMessage(inputValue);
    setInputValue('');
  };

  const handleResearchSubmit = (e) => {
    e.preventDefault();
    if (!researchQuery.trim()) return;
    startResearch(researchQuery);
    setResearchQuery('');
    setActiveTab('chat'); // Switch to chat to see results
  };

  const handleEvaluateSubmit = (e) => {
    e.preventDefault();
    if (!playerName.trim()) return;
    evaluatePlayer(playerName);
    setPlayerName('');
    setActiveTab('chat'); // Switch to chat to see results
  };

  const handleScoopSubmit = (e) => {
    e.preventDefault();
    if (!scoopQuery.trim()) return;
    searchScoop(scoopQuery, scoopSearchType);
    setScoopQuery('');
    setActiveTab('chat'); // Switch to chat to see results
  };

  const handleComprehensiveSearch = () => {
    if (!scoopQuery.trim()) return;
    comprehensiveSearch(scoopQuery);
    setScoopQuery('');
    setActiveTab('chat');
  };

  const handlePromptClick = (prompt) => {
    sendMessage(prompt);
  };

  return (
    <div className="h-full flex flex-col bg-[#0A0A0A]">
      {/* Header */}
      <div className="border-b px-4 py-3 border-[#1A1A1A] bg-[#111111]">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-2xl bg-orange-500/20">
              {WOJ_AVATAR}
            </div>
            <div>
              <h2 className="font-semibold text-[#F5F5F5]">
                {WOJ_NAME}
              </h2>
              <p className="text-xs text-[#999999]">
                {WOJ_TITLE}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {activeResearch && (
              <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full animate-pulse bg-blue-500/20 text-blue-400">
                <Loader2 className="h-3 w-3 animate-spin" />
                Research in progress
              </span>
            )}
            {activeExaSearch && (
              <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full animate-pulse bg-purple-500/20 text-purple-400">
                <Loader2 className="h-3 w-3 animate-spin" />
                Exa searching...
              </span>
            )}
            <button
              onClick={clearChat}
              className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg transition-colors text-[#999999] hover:text-red-400 hover:bg-red-500/10"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Clear
            </button>
          </div>
        </div>

        {/* Quick badges */}
        <div className="flex flex-wrap gap-2">
          <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[#1A1A1A] text-[#999999]">
            <Target className="h-2.5 w-2.5" />
            Transfer Portal Expert
          </span>
          <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[#1A1A1A] text-[#999999]">
            <TrendingUp className="h-2.5 w-2.5" />
            Advanced Analytics
          </span>
          <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[#1A1A1A] text-[#999999]">
            <DollarSign className="h-2.5 w-2.5" />
            NIL Valuation
          </span>
        </div>
      </div>

      {/* Messages area */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4"
      >
        <div className="space-y-4 py-4">
          {messages.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-5xl mb-4">{WOJ_AVATAR}</div>
              <p className="text-lg font-medium mb-2 text-[#F5F5F5]">
                "I think about the Moneyball scene all the time..."
              </p>
              <p className="text-sm max-w-md mx-auto mb-6 text-[#999999]">
                Ask me about player evaluations, transfer portal strategy,
                or recruiting analysis. I'll give you the Bonaventure perspective
                on getting high-major talent for mid-major money.
              </p>

              {/* Sample prompts */}
              <div className="grid grid-cols-2 gap-2 max-w-md mx-auto">
                {CHAT_PROMPTS.map((prompt, idx) => (
                  <button
                    key={idx}
                    onClick={() => handlePromptClick(prompt.text)}
                    className="flex items-center gap-2 p-3 rounded-[14px] text-left text-sm transition-all bg-[#1A1A1A]/50 hover:bg-[#1A1A1A] text-[#999999]"
                  >
                    <span>{prompt.icon}</span>
                    <span>{prompt.text}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map((msg) => (
                <MessageBubble
                  key={msg.id}
                  message={msg}
                />
              ))}
            </>
          )}

          {/* Loading indicator */}
          {isLoading && !activeResearch && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-lg bg-orange-500/20">
                {WOJ_AVATAR}
              </div>
              <div className="rounded-2xl px-4 py-2.5 bg-[#1A1A1A]">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-[#999999]" />
                  <span className="text-sm text-[#999999]">
                    Woj is analyzing...
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Input area with tabs */}
      <div className="border-t p-4 border-[#1A1A1A] bg-[#111111]">
        {/* Tab buttons */}
        <div className="flex gap-2 mb-3">
          <TabButton
            active={activeTab === 'chat'}
            onClick={() => setActiveTab('chat')}
            icon={MessageSquare}
          >
            Chat
          </TabButton>
          <TabButton
            active={activeTab === 'research'}
            onClick={() => setActiveTab('research')}
            icon={Search}
          >
            Deep Research
          </TabButton>
          <TabButton
            active={activeTab === 'evaluate'}
            onClick={() => setActiveTab('evaluate')}
            icon={UserCheck}
          >
            Evaluate Player
          </TabButton>
          <TabButton
            active={activeTab === 'scoop'}
            onClick={() => setActiveTab('scoop')}
            icon={Zap}
          >
            Exa Scoop
          </TabButton>
        </div>

        {/* Chat input */}
        {activeTab === 'chat' && (
          <form onSubmit={handleChatSubmit} className="flex gap-2">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Ask Woj about recruiting, transfers, or strategy..."
              disabled={isLoading}
              className="flex-1 px-4 py-2.5 rounded-[14px] text-sm outline-none transition-colors bg-[#1A1A1A] text-[#F5F5F5] placeholder-[#555555] focus:ring-2 focus:ring-orange-500/50"
            />
            <button
              type="submit"
              disabled={isLoading || !inputValue.trim()}
              className={`
                p-2.5 rounded-[14px] transition-colors
                ${isLoading || !inputValue.trim()
                  ? 'bg-[#1A1A1A] text-[#555555]'
                  : 'bg-orange-500 hover:bg-orange-600 text-white'}
              `}
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        )}

        {/* Research input */}
        {activeTab === 'research' && (
          <div>
            <form onSubmit={handleResearchSubmit} className="flex gap-2">
              <input
                type="text"
                value={researchQuery}
                onChange={(e) => setResearchQuery(e.target.value)}
                placeholder="Enter a topic for deep research..."
                disabled={isLoading}
                className="flex-1 px-4 py-2.5 rounded-[14px] text-sm outline-none transition-colors bg-[#1A1A1A] text-[#F5F5F5] placeholder-[#555555] focus:ring-2 focus:ring-blue-500/50"
              />
              <button
                type="submit"
                disabled={isLoading || !researchQuery.trim()}
                className={`
                  flex items-center gap-1.5 px-4 py-2.5 rounded-[14px] text-sm font-medium transition-colors
                  ${isLoading || !researchQuery.trim()
                    ? 'bg-[#1A1A1A] text-[#555555]'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'}
                `}
              >
                <Search className="h-4 w-4" />
                Research
              </button>
            </form>
            <p className="text-xs mt-2 text-[#555555]">
              Deep research may take 2-5 minutes for comprehensive analysis.
            </p>
            {/* Quick research topics */}
            <div className="flex flex-wrap gap-1.5 mt-2">
              {RESEARCH_TOPICS.map((topic, idx) => (
                <button
                  key={idx}
                  onClick={() => setResearchQuery(topic)}
                  className="text-[10px] px-2 py-1 rounded-full transition-colors bg-[#1A1A1A] text-[#999999] hover:bg-[rgba(255,255,255,0.04)]"
                >
                  {topic}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Evaluate input */}
        {activeTab === 'evaluate' && (
          <div>
            <form onSubmit={handleEvaluateSubmit} className="flex gap-2">
              <input
                type="text"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                placeholder="Enter player name (e.g., 'Cooper Flagg')"
                disabled={isLoading}
                className="flex-1 px-4 py-2.5 rounded-[14px] text-sm outline-none transition-colors bg-[#1A1A1A] text-[#F5F5F5] placeholder-[#555555] focus:ring-2 focus:ring-green-500/50"
              />
              <button
                type="submit"
                disabled={isLoading || !playerName.trim()}
                className={`
                  flex items-center gap-1.5 px-4 py-2.5 rounded-[14px] text-sm font-medium transition-colors
                  ${isLoading || !playerName.trim()
                    ? 'bg-[#1A1A1A] text-[#555555]'
                    : 'bg-green-600 hover:bg-green-700 text-white'}
                `}
              >
                <UserCheck className="h-4 w-4" />
                Evaluate
              </button>
            </form>
            <p className="text-xs mt-2 text-[#555555]">
              Get a full Moneyball-style evaluation with fit assessment for St. Bonaventure.
            </p>
          </div>
        )}

        {/* Exa Scoop input */}
        {activeTab === 'scoop' && (
          <div>
            <form onSubmit={handleScoopSubmit} className="space-y-3">
              {/* Search type selector */}
              <div className="flex flex-wrap gap-1.5">
                {EXA_SEARCH_TYPES.map((type) => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setScoopSearchType(type.value)}
                      className={`
                        flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs transition-all
                        ${scoopSearchType === type.value
                          ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                          : 'bg-[#1A1A1A] text-[#999999] hover:bg-[rgba(255,255,255,0.04)]'}
                      `}
                      title={type.description}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      {type.label}
                    </button>
                  );
                })}
              </div>

              {/* Search input */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={scoopQuery}
                  onChange={(e) => setScoopQuery(e.target.value)}
                  placeholder="Search player, team, or topic..."
                  disabled={isLoading}
                  className="flex-1 px-4 py-2.5 rounded-[14px] text-sm outline-none transition-colors bg-[#1A1A1A] text-[#F5F5F5] placeholder-[#555555] focus:ring-2 focus:ring-purple-500/50"
                />
                <button
                  type="submit"
                  disabled={isLoading || !scoopQuery.trim()}
                  className={`
                    flex items-center gap-1.5 px-4 py-2.5 rounded-[14px] text-sm font-medium transition-colors
                    ${isLoading || !scoopQuery.trim()
                      ? 'bg-[#1A1A1A] text-[#555555]'
                      : 'bg-purple-600 hover:bg-purple-700 text-white'}
                  `}
                >
                  <Zap className="h-4 w-4" />
                  Search
                </button>
              </div>

              {/* Comprehensive search button */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleComprehensiveSearch}
                  disabled={isLoading || !scoopQuery.trim()}
                  className={`
                    flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors
                    ${isLoading || !scoopQuery.trim()
                      ? 'bg-[#1A1A1A] text-[#555555]'
                      : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white'}
                  `}
                >
                  <Globe className="h-3.5 w-3.5" />
                  Full Intel Report (all sources)
                </button>
                <span className="text-[10px] text-[#555555]">
                  Searches news, stats, and socials
                </span>
              </div>
            </form>

            <p className="text-xs mt-3 text-[#555555]">
              Powered by <span className="font-medium">Exa</span> semantic search. Woj will analyze results.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
